import * as React from "react";
import { ScanLine, ShieldCheck, Flag, Check, X } from "lucide-react";
import { cn } from "../cn";
import { Tooltip } from "./Tooltip";
import type { AIFinding, AIFindingSeverity, AIReviewState } from "@/domain/types";

// The Heimdall presence kit (Heimdall brief). Heimdall is an advisory analyst woven
// through the surfaces — these components carry its review badge, its findings and its
// attribution. They are presentational; the analyst name is passed in so a workspace can
// rename the presence. No Norse imagery: the identity is institutional, the name carries it.

const REVIEW_META: Record<AIReviewState, { label: string; tone: string; bg: string; dot: string; help: (n: string) => string }> = {
  passed: {
    label: "Checks passed",
    tone: "text-ok",
    bg: "bg-okSoft",
    dot: "bg-ok",
    help: (n) => `${n} reviewed this and raised nothing.`,
  },
  flagged: {
    label: "Flags raised",
    tone: "text-warn",
    bg: "bg-warnSoft",
    dot: "bg-warn",
    help: (n) => `${n} raised one or more findings here. A person decides what to do.`,
  },
  pending: {
    label: "Not yet reviewed",
    tone: "text-mute",
    bg: "bg-neutralSoft",
    dot: "bg-faint",
    help: (n) => `${n} has not reviewed this yet.`,
  },
};

/** Heimdall's review badge for an ask or evidence item (Heimdall brief §2). Advisory. */
export function HeimdallBadge({
  state,
  count,
  name = "Heimdall",
  className,
}: {
  state: AIReviewState;
  count?: number;
  name?: string;
  className?: string;
}) {
  const m = REVIEW_META[state];
  const Icon = state === "passed" ? ShieldCheck : state === "flagged" ? Flag : ScanLine;
  return (
    <Tooltip label={m.help(name)}>
      <span
        className={cn(
          "inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-2 text-[11px] font-medium leading-none",
          m.bg,
          m.tone,
          className,
        )}
      >
        <Icon className="h-3 w-3" aria-hidden />
        {m.label}
        {state === "flagged" && count ? <span className="tnum">· {count}</span> : null}
      </span>
    </Tooltip>
  );
}

const SEVERITY_META: Record<AIFindingSeverity, { label: string; variant: string }> = {
  info: { label: "Note", variant: "bg-infoSoft text-info" },
  attention: { label: "Attention", variant: "bg-warnSoft text-warn" },
  serious: { label: "Serious", variant: "bg-badSoft text-bad" },
};

const CATEGORY_LABEL: Record<AIFinding["category"], string> = {
  integrity: "Integrity",
  plausibility: "Plausibility",
  completeness: "Completeness",
  compliance: "Compliance",
  progress: "Progress",
};

export function SeverityChip({ severity }: { severity: AIFindingSeverity }) {
  const m = SEVERITY_META[severity];
  return (
    <span className={cn("inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide", m.variant)}>
      {m.label}
    </span>
  );
}

/**
 * A single Heimdall finding: severity, category, the plain-English statement, what it cites,
 * and accept / dismiss controls. Citations render as a compact line here; the side-by-side
 * evidence view that opens from "Cited evidence" arrives with the queues (Heimdall brief §4).
 */
export function FindingCard({
  finding,
  name = "Heimdall",
  decidedByName,
  citationsLabel,
  onAccept,
  onDismiss,
  onOpenCitations,
  className,
}: {
  finding: AIFinding;
  name?: string;
  decidedByName?: string;
  citationsLabel?: string;
  onAccept?: () => void;
  onDismiss?: () => void;
  onOpenCitations?: () => void;
  className?: string;
}) {
  return (
    <div className={cn("rounded-lg border border-line bg-card p-3", className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <SeverityChip severity={finding.severity} />
          <span className="text-[11px] font-medium text-mute">{CATEGORY_LABEL[finding.category]}</span>
        </div>
        <span className="text-[11px] text-faint">{finding.confidence === "high" ? "High confidence" : "Moderate confidence"}</span>
      </div>
      <p className="mt-2 text-sm text-ink">{finding.statement}</p>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-mute">
        <span>Flagged by {name}</span>
        {citationsLabel && (
          <button
            type="button"
            onClick={onOpenCitations}
            disabled={!onOpenCitations}
            className={cn("underline-offset-2", onOpenCitations ? "text-brand hover:underline" : "text-faint")}
          >
            {citationsLabel}
          </button>
        )}
      </div>

      {finding.status === "open" ? (
        (onAccept || onDismiss) && (
          <div className="mt-3 flex items-center gap-2">
            {onAccept && (
              <button
                type="button"
                onClick={onAccept}
                className="inline-flex items-center gap-1.5 rounded-md border border-line px-2.5 py-1 text-xs font-medium text-ink hover:bg-subtle"
              >
                <Check className="h-3.5 w-3.5" aria-hidden /> Accept
              </button>
            )}
            {onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                className="inline-flex items-center gap-1.5 rounded-md border border-line px-2.5 py-1 text-xs font-medium text-mute hover:bg-subtle"
              >
                <X className="h-3.5 w-3.5" aria-hidden /> Dismiss
              </button>
            )}
          </div>
        )
      ) : (
        <div className="mt-2 border-t border-line pt-2 text-[11px] text-mute">
          {finding.status === "accepted"
            ? `Accepted${decidedByName ? ` by ${decidedByName}` : ""}.`
            : `Dismissed${decidedByName ? ` by ${decidedByName}` : ""}${finding.dismissReason ? `, ${finding.dismissReason}` : ""}.`}
        </div>
      )}
    </div>
  );
}

/**
 * Attribution line pairing Heimdall with the human who confirmed the outcome — the litmus
 * for any money-release context: AI-reviewed and human-confirmed, never AI alone.
 */
export function HeimdallAttribution({
  name = "Heimdall",
  reviewerName,
  firstMention,
  className,
}: {
  name?: string;
  reviewerName?: string;
  /** On the first mention of a surface, pair the name with its plain descriptor. */
  firstMention?: boolean;
  className?: string;
}) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-[11px] text-mute", className)}>
      <ShieldCheck className="h-3.5 w-3.5 text-brand" aria-hidden />
      {firstMention ? `${name}, the AI analyst` : name}
      {reviewerName ? ` · confirmed by ${reviewerName}` : ""}
    </span>
  );
}

/** The staged "reviewing" indicator shown for 2–4s after a live submission. Respects reduced motion. */
export function AnalystReviewingIndicator({ name = "Heimdall", className }: { name?: string; className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] font-medium text-brand", className)} role="status" aria-live="polite">
      <ScanLine className="h-3.5 w-3.5 animate-pulse motion-reduce:animate-none" aria-hidden />
      {name} reviewing…
    </span>
  );
}
